// Renders the Sentinel workspace and wires its actions.

import { TicketStatusFilter } from '@/modules/sentinel-workspace/ticket-status-filter';
import { useAssignToMe } from '@/hooks/use-sentinel-assign-to-me';
import { canUnassignTicket, useUnassignTicket } from '@/hooks/use-sentinel-unassign';
import {
  canManageSentinelMembersAtom,
  isSentinelRoleAtom,
  resolvedSentinelSectionAtom,
  sentinelViewCountsAtom,
  useSentinelRouting,
  type SentinelTicketSection,
} from '@/hooks/use-sentinel-routing';
import { useAtom, useAtomValue } from 'jotai';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { currentUserAtom } from '@/hooks/use-auth';
import { SENTINEL_WORKSPACE_PATH, sentinelTicketIdFromUrl } from '@/lib/landing';
import { usePermissions } from '@/hooks/use-permissions';
import { useSentinelSettingsGate } from '@/hooks/use-sentinel-dashboard-enabled';
import {
  COMPOSER_STATUS,
  WORKSPACE_MODAL,
  selectedTicketIdAtom,
  sentinelTicketTotalAtom,
  ticketListCollapsedAtom,
  useSentinelComposer,
  useSentinelConversation,
  useSentinelTickets,
  useSubscriptionIntegrations,
  useTicketDisplaySettings,
} from '@/hooks/use-sentinel-workspace';
import { useTicketPresence } from '@/hooks/use-ticket-presence';
import { selectedBrandAtom } from '@/stores/sentinel-workspace.store';
import { TicketList } from '@/components/shared/ticket-list';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ChevronDown, PanelLeft, RefreshCw, UserMinus, UserPlus } from 'lucide-react';
import { useAutomationReplies } from '@/hooks/use-automation-replies';
import type { AutomationReplyFilter } from '@/services/automation-reply.service';
import { AutomationQueue } from './automation-review';
import { Button } from '@/components/ui/button';
import { ChatThread, ConversationHeader } from './chat-thread';
import { AiChatOverlayHost, ComposerPanel, type PendingSend } from './composer-panel';
import { DetailsPanel, DETAILS_MIN_WIDTH } from './details-panel';
import { activityRunsFromApi, rootKeyOf, runIdsByReply } from './activity';
import { detailsPanelInputs } from './details-panel-inputs';
import { SimilarTicketPanel } from './similar-ticket-panel';
import { OpenInHelpdeskButton } from './open-in-helpdesk-button';
import { buildThreadMessages, latestPendingActionsTurn } from './sentinel-workspace.helpers';
import {
  supportsCustomerTickets,
  supportsProviderAttachments,
  supportsProviderHandoff,
  type FeedbackVerdict,
  type RejectReason,
  type SimilarTicketMatch,
} from './types';
import {
  FeedbackForAiModal,
  OverrideReasonModal,
  RejectModal,
  SendGateModal,
  type RejectHandoff,
  type RejectModalVariant,
} from './workspace-modals';
import type { TicketFieldValue } from '@/types';
import { FollowupsPanel, FollowupViewMenu } from './followups/followups-panel';
import { FollowupDrawer } from './followups/followup-drawer';
import { followupCountsQueryAtom } from './followups/followup-state';
import { TicketFollowupControl } from './followups/ticket-followup';
import { queryClientAtom } from 'jotai-tanstack-query';
import { ListIconButton } from './list-icon-button';
import { ViewPicker } from './view-picker';
import { VIEW_ORDER, type ViewCounts, type ViewOption, type WorkspaceView } from './view-options';

/** `?section=followups` swaps the ticket panes for that section, so it survives a refresh and can be linked. */
const SECTION_PARAM = 'section';
const URL_SECTIONS = ['followups'] as const;
type UrlSection = (typeof URL_SECTIONS)[number];
const isUrlSection = (value: string | null): value is UrlSection => URL_SECTIONS.includes(value as UrlSection);

function DetailsPanelSkeleton() {
  return (
    <div className="flex w-[320px] shrink-0 flex-col gap-4 border-l border-gray-500 bg-white p-4">
      <Skeleton className="h-5 w-24 rounded-md" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32 rounded-md" />
        <Skeleton className="h-4 w-44 rounded-md" />
      </div>
      <Skeleton className="h-28 w-full rounded-2xl" />
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  );
}

/** `onExpandList` is set while the list is collapsed: this pane has no header to hold the button. */
function EmptyState({ onExpandList }: { onExpandList?: () => void }) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-6">
      {onExpandList && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onExpandList}
          className="absolute left-4 top-4 text-gray-900"
          aria-label="Expand ticket list"
        >
          <PanelLeft className="size-[14px]" />
        </Button>
      )}
      <div className="flex size-20 items-center justify-center rounded-2xl border border-gray-300 bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
        <img src="/assets/brand/kim-ai-mark-mono.svg" alt="Kim AI" className="size-10" />
      </div>
      <p className="text-[13px] leading-[18px] text-text-body">Please choose a ticket to get started</p>
    </div>
  );
}

/** White bubbles like real messages — grey would vanish on this background. */
function ThreadSkeleton() {
  // Bubble-sized, not thread-sized: the real bubbles hug their text, so the stand-ins do too.
  const customer = (width: string, lines: string[]) => (
    <div className="flex items-start gap-1.5 pl-4 pr-24">
      <Skeleton className="size-8 shrink-0 rounded-full border border-gray-300 bg-white" />
      <div className={cn('max-w-full rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]', width)}>
        <div className="flex animate-pulse flex-col gap-2">
          {lines.map((line) => (
            <div key={line} className={cn('h-3 rounded bg-gray-200', line)} />
          ))}
        </div>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col gap-4 pt-4">
      {customer('w-[320px]', ['w-3/4', 'w-1/2'])}
      <div className="flex items-start justify-end gap-1.5 pl-24 pr-4">
        <div className="w-[280px] max-w-full rounded-2xl border border-gray-300 bg-white p-4">
          <div className="flex animate-pulse flex-col items-end gap-2">
            <div className="h-3 w-2/3 rounded bg-gray-200" />
            <div className="h-3 w-2/5 rounded bg-gray-200" />
          </div>
        </div>
        <Skeleton className="size-8 shrink-0 rounded-full border border-gray-300 bg-white" />
      </div>
      {customer('w-[240px]', ['w-3/5'])}
    </div>
  );
}

function ComposerSkeleton() {
  return (
    <div className="absolute inset-x-0 bottom-0 p-4">
      <div className="h-[52px] w-full animate-pulse rounded-2xl border border-gray-300 bg-white" />
    </div>
  );
}

// The same shell it is about to become, shown while the settings call decides.
function WorkspaceSkeleton({ canEdit, hasTicket }: { canEdit: boolean; hasTicket: boolean }) {
  const noop = () => {};
  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      <TicketList
        items={[]}
        selectedId={null}
        onSelect={noop}
        isLoading
        onCollapse={noop}
        // Static stand-ins for the view menu and refresh button, so the header does not shift on load.
        title={
          <span className="-ml-2 flex h-8 items-center gap-1.5 px-2 text-[16px] font-medium leading-[1.1] text-text-body">
            My tickets
            <Skeleton className="h-3 w-7 rounded" />
            <ChevronDown className="size-3.5 text-gray-700" aria-hidden />
          </span>
        }
        headerActions={
          <span className="flex size-7 items-center justify-center text-gray-700" aria-hidden>
            <RefreshCw className="size-[14px]" />
          </span>
        }
        toolbar={<Skeleton className="h-8 w-full rounded-full" />}
      />

      {/* A floor, not min-w-0: it is what makes the details panel shrink first. */}
      <div className="flex min-w-[420px] flex-1 flex-col">
        {hasTicket ? (
          <>
            <ConversationHeader
              displayId={null}
              listCollapsed={false}
              onExpandList={noop}
              detailsCollapsed={false}
              onExpandDetails={noop}
            />
            <div className="relative flex min-h-0 flex-1 flex-col bg-gray-100">
              <div className="min-h-0 flex-1 overflow-y-auto">
                <ThreadSkeleton />
              </div>
              {canEdit && <ComposerSkeleton />}
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      {hasTicket && <DetailsPanelSkeleton />}
    </div>
  );
}

function ConversationError({ onBack, onExpandList }: { onBack: () => void; onExpandList?: () => void }) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-3">
      {onExpandList && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onExpandList}
          className="absolute left-4 top-4 text-gray-900"
          aria-label="Expand ticket list"
        >
          <PanelLeft className="size-[14px]" />
        </Button>
      )}
      <p className="text-[15px] font-medium text-text-body">Unable to load this ticket</p>
      <p className="text-[13px] leading-[18px] text-gray-800">It may no longer exist or you may not have access to it.</p>
      <Button variant="outline" onClick={onBack}>
        Back to tickets
      </Button>
    </div>
  );
}

/** The whole review queue, any date: the same query the queue itself runs before a date is picked. */
const REVIEW_QUEUE: AutomationReplyFilter = { review: 'pending', outcome: 'sent' };

/** The ticket list's own views; the others are pages of their own. */
const LIST_OPTIONS: ReadonlySet<ViewOption> = new Set<ViewOption>(['mine', 'all', 'unassigned']);

/**
 * The list title: the current view as a menu, each view with its count. My tickets is a
 * Sentinel's own queue, so anyone else (an administrator) never gets it.
 */
function WorkspaceViewPicker({ view, onChange }: { view: WorkspaceView; onChange: (view: WorkspaceView) => void }) {
  const { total: review } = useAutomationReplies(REVIEW_QUEUE);
  const listTotal = useAtomValue(sentinelTicketTotalAtom);
  const viewCounts = useAtomValue(sentinelViewCountsAtom);
  const followups = useAtomValue(followupCountsQueryAtom).data;
  const routing = useSentinelRouting();

  const current: ViewOption =
    view === 'tickets'
      ? routing.section === 'mine' || routing.section === 'unassigned'
        ? routing.section
        : 'all'
      : view;
  const options = VIEW_ORDER.filter((option) => {
    if (option === 'mine') return routing.routingEnabled && routing.isSentinel;
    if (option === 'unassigned') return routing.routingEnabled;
    return true;
  });
  const total = (key: SentinelTicketSection | 'followup') => {
    const counts = viewCounts?.[key];
    return counts ? counts.open + counts.closed : undefined;
  };
  const counts: ViewCounts = {
    mine: total('mine'),
    all: total('all'),
    unassigned: total('unassigned'),
    automation: review,
    followups: total('followup') ?? (followups && followups.waiting + followups.held),
  };
  // The open list's own total is the exact one: it follows the status filter and the search.
  if (view === 'tickets' && listTotal !== undefined) counts[current] = listTotal;

  const pick = (option: ViewOption) => {
    if (LIST_OPTIONS.has(option)) {
      routing.setSection(option as SentinelTicketSection);
      onChange('tickets');
      return;
    }
    onChange(option as WorkspaceView);
  };

  return (
    <ViewPicker
      current={current}
      options={options}
      counts={counts}
      onChange={pick}
      followupMenu={<FollowupViewMenu onPick={() => onChange('followups')} />}
    />
  );
}

function EnabledSentinelWorkspace({ canEdit }: { canEdit: boolean }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Follow-ups is a section in the URL; the other two views are local to the page.
  const [listView, setListView] = useState<Exclude<WorkspaceView, UrlSection>>('tickets');
  const section = searchParams.get(SECTION_PARAM);
  const view: WorkspaceView = isUrlSection(section) ? section : listView;
  const changeView = (next: WorkspaceView) => {
    if (next !== view) {
      // Another view closes the open ticket: the centre shows "choose a ticket" until one is picked.
      const params = new URLSearchParams(searchParams);
      if (isUrlSection(next)) params.set(SECTION_PARAM, next);
      else params.delete(SECTION_PARAM);
      navigate(`${SENTINEL_WORKSPACE_PATH}${params.size ? `?${params}` : ''}`, { replace: true });
    }
    if (!isUrlSection(next)) setListView(next);
  };
  const viewSwitch = <WorkspaceViewPicker view={view} onChange={changeView} />;

  if (view === 'followups') return <FollowupsPanel canEdit={canEdit} viewSwitch={viewSwitch} />;
  return <TicketWorkspace canEdit={canEdit} view={view} viewSwitch={viewSwitch} />;
}

function TicketWorkspace({ canEdit, view, viewSwitch }: { canEdit: boolean; view: WorkspaceView; viewSwitch: React.ReactNode }) {
  const queryClient = useAtomValue(queryClientAtom);
  const [listCollapsed, setListCollapsed] = useAtom(ticketListCollapsedAtom);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  // Phones show one pane at a time: the list, or the open ticket; its details open as a sheet.
  const isMobile = useIsMobile();
  const expandList = listCollapsed && !isMobile ? () => setListCollapsed(false) : undefined;
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [detailsWidth, setDetailsWidth] = useState(DETAILS_MIN_WIDTH);
  // Reply held while the send-time feedback modal is open.
  const [pendingSend, setPendingSend] = useState<PendingSend | null>(null);
  // Counts Send clicks: each one gets a fresh override reason form, while Back to the gate modal keeps it.
  const [sendClicks, setSendClicks] = useState(0);
  // True while a send or note request is in flight — blocks duplicate submits.
  const [sendBusy, setSendBusy] = useState(false);

  const tickets = useSentinelTickets();
  const {
    conversation,
    detail,
    isLoading: conversationLoading,
    error: conversationError,
    refresh: refreshConversation,
  } = useSentinelConversation();
  const connectedSubscriptions = useSubscriptionIntegrations();
  // Read for the ticket's own brand; until they load, times stay in the viewer's zone and the blueprint bar shows.
  const { timeZone, blueprintEnabled } = useTicketDisplaySettings();
  const blueprintsOff = blueprintEnabled === false;
  const { composer, modal, setModal, versions, sentReply, sentResponseId, privateNotes, aiChat, refineLimitHit, refineReady, regenerateReady, blueprintLimitHit, toolTrace, sendGate, actions } =
    useSentinelComposer(conversation, detail);
  // Off in the Automation view: the ticket left open behind it is not being looked at.
  const presence = useTicketPresence(view === 'tickets' ? conversation?.id ?? null : null);
  // Only a live Gorgias ticket is written to Gorgias, which refuses a close while a required field is empty.
  const liveGorgias = detail?.ticket.mode === 'live' && detail.ticket.source_provider === 'gorgias';
  // Only a Sentinel takes an unassigned ticket ("Assign to me").
  const { role } = usePermissions();
  const isSentinel = role === 'sentinel';
  const ticketSection = useAtomValue(resolvedSentinelSectionAtom);
  const viewCounts = useAtomValue(sentinelViewCountsAtom);
  const showOwners = ticketSection === 'all';
  // "Assign to me": a Sentinel takes a ticket nobody owns yet.
  const assignToMe = useAssignToMe(tickets.selectTicket);
  const unassignedIds = useMemo(
    () => new Set(tickets.tickets.filter((ticket) => ticket.routerState === 'unassigned').map((ticket) => ticket.id)),
    [tickets.tickets],
  );
  const canAssign = (ticketId: number) => isSentinel && unassignedIds.has(ticketId);
  // In the open ticket's header only; the list rows have no menu of their own.
  const assignHeaderButton = (ticketId: number) =>
    canAssign(ticketId) ? (
      <Button
        variant="outline"
        size="sm"
        disabled={assignToMe.pendingTicketId !== null}
        onClick={() => void assignToMe.assign(ticketId)}
        className="h-8 gap-1.5 px-3 text-[13px]"
      >
        <UserPlus className="size-3.5" />
        {assignToMe.pendingTicketId === ticketId ? 'Assigning…' : 'Assign to me'}
      </Button>
    ) : null;
  // "Unassign": the owner, or an administrator, gives the open ticket up. It stays open and refreshes.
  const currentUser = useAtomValue(currentUserAtom);
  const isAdmin = useAtomValue(canManageSentinelMembersAtom);
  const isSentinelRole = useAtomValue(isSentinelRoleAtom);
  const refreshOpenTicket = useCallback(() => void refreshConversation(), [refreshConversation]);
  const unassignTicket = useUnassignTicket(refreshOpenTicket);
  const canUnassign = (ticketId: number) =>
    canUnassignTicket({
      ownerId: tickets.tickets.find((ticket) => ticket.id === ticketId)?.ownerId ?? null,
      currentUserId: currentUser?.id,
      isAdmin,
      isSentinel: isSentinelRole,
    });
  const unassignHeaderButton = (ticketId: number) =>
    canUnassign(ticketId) ? (
      <Button
        variant="outline"
        size="sm"
        disabled={unassignTicket.pendingTicketId !== null}
        onClick={() => void unassignTicket.unassign(ticketId)}
        className="h-8 gap-1.5 px-3 text-[13px]"
      >
        <UserMinus className="size-3.5" />
        {unassignTicket.pendingTicketId === ticketId ? 'Unassigning…' : 'Unassign'}
      </Button>
    ) : null;

  // Built once per data change — the panel inputs walk every tool result.
  // An approval prompt carries the order it is about, so a HITL ticket shows one without a lookup tool.
  // New row objects each render would redraw every row.
  const listItems = useMemo(
    () =>
      tickets.tickets.map((ticket) => ({
        id: ticket.id,
        subject: ticket.subject,
        customerName: ticket.customerName,
        preview: ticket.preview,
        category: ticket.category,
        timestamp: ticket.lastActivityAt,
        // Only the admin "All" view names owners; elsewhere every row is the viewer's or nobody's.
        ...(showOwners ? { ownerName: ticket.ownerName } : {}),
        unread: tickets.unreadTicketIds.has(ticket.id),
      })),
    [tickets.tickets, tickets.unreadTicketIds, showOwners],
  );

  const pendingActionsTurn = useMemo(() => latestPendingActionsTurn(detail?.aiEventLogs ?? []), [detail?.aiEventLogs]);
  const [openSimilarTicket, setOpenSimilarTicket] = useState<SimilarTicketMatch | null>(null);
  // A match belongs to one ticket; carried onto the next it would be refused with a 403.
  useEffect(() => setOpenSimilarTicket(null), [conversation?.id]);

  // The composer floats over the thread, so the thread reserves exactly its height. Re-observed
  // per ticket because the composer unmounts while the next conversation loads.
  // Set on the scroller, not in state: a fold resizes the composer every frame, and a re-render per frame stutters.
  const composerRef = useRef<HTMLDivElement | null>(null);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      if (threadScrollRef.current) threadScrollRef.current.style.paddingBottom = `${node.offsetHeight}px`;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [canEdit, conversation?.id]);

  const threadMessages = useMemo(
    () => buildThreadMessages(conversation, sentReply, privateNotes, sentResponseId),
    [conversation, privateNotes, sentReply, sentResponseId],
  );

  // The panel shows one AI generation: the current one (the ticket's root trace), until a reply's View activity picks another.
  const activityRuns = useMemo(() => (detail ? activityRunsFromApi(detail, threadMessages) : []), [detail, threadMessages]);
  const activityRunByReply = useMemo(() => runIdsByReply(activityRuns), [activityRuns]);
  const [activityRunId, setActivityRunId] = useState<number | null>(null);
  useEffect(() => setActivityRunId(null), [conversation?.id]);
  const defaultRunId =
    activityRuns.find((run) => run.rootKey === detail?.ticket.root_trace_id)?.id ?? activityRuns.at(-1)?.id ?? null;
  const selectedRunId = activityRunId ?? defaultRunId;
  const selectedRun = activityRuns.find((run) => run.id === selectedRunId) ?? null;
  // A live generation's newest steps are not saved yet; they belong to the latest run, never an earlier one.
  const isLatestRun = selectedRun !== null && selectedRun.id === activityRuns.at(-1)?.id;
  const otherRunStepIds = useMemo(
    () => new Set(activityRuns.filter((run) => run.id !== selectedRunId).flatMap((run) => run.steps.map((step) => step.id))),
    [activityRuns, selectedRunId],
  );
  const openActivity = (runId: number) => {
    setActivityRunId(runId);
    if (isMobile) setMobileDetailsOpen(true);
    else setDetailsCollapsed(false);
  };

  // Built once per data change — the panel inputs walk every tool result. Shared with Review and
  // Follow-ups (ticket-context-panel), so a ticket reads the same wherever it is opened. Here the
  // cards read only the selected generation: its event logs and its tool steps.
  const panelInputs = useMemo(() => {
    if (!conversation) return null;
    if (!selectedRun || !detail) return detailsPanelInputs({ conversation, detail, toolTrace, connectedSubscriptions });
    const runStepIds = new Set(selectedRun.steps.map((step) => step.id));
    return detailsPanelInputs({
      // The provider's order and subscriptions are fetched live, so they describe only the current generation.
      conversation: selectedRun.id === defaultRunId ? conversation : { ...conversation, order: null, subscriptions: [] },
      detail: { ...detail, aiEventLogs: detail.aiEventLogs.filter((event) => rootKeyOf(event) === selectedRun.rootKey) },
      toolTrace: toolTrace.filter((step) => runStepIds.has(step.id) || (isLatestRun && !otherRunStepIds.has(step.id))),
      connectedSubscriptions,
    });
  }, [conversation, detail, toolTrace, connectedSubscriptions, selectedRun, defaultRunId, isLatestRun, otherRunStepIds]);
  const detailsStorefront = panelInputs?.storefront ?? null;
  const detailsSubscriptions = panelInputs?.subscriptions ?? [];
  const similarTickets = useMemo(() => panelInputs?.similarTickets ?? [], [panelInputs]);
  const detailsTrace = panelInputs?.trace ?? [];

  const rejectVariant: RejectModalVariant | null =
    modal === WORKSPACE_MODAL.rejectBlueprint || modal === WORKSPACE_MODAL.rejectResponse || modal === WORKSPACE_MODAL.assign
      ? modal
      : null;

  const handleRejectConfirm = async (
    reason: RejectReason,
    providerAgentId: string | null,
    otherText: string | null,
    handoff: RejectHandoff,
  ) => {
    let succeeded = false;
    if (modal === WORKSPACE_MODAL.assign && providerAgentId !== null) {
      succeeded = await actions.assign(reason, providerAgentId);
    } else {
      succeeded = await actions.reject({
        stage: modal === WORKSPACE_MODAL.rejectResponse ? 'response' : 'blueprint',
        reason,
        otherText,
        providerAgentId,
        tags: handoff.tags,
        note: handoff.note,
      });
    }
    if (succeeded) tickets.selectTicket(null);
  };

  const handleFeedbackClose = () => {
    setModal(null);
    setPendingSend(null);
  };

  /** Send and Send & Close. With the gate on, the reply is checked first; a blocked one opens the gate modal. */
  const handleSend = async (next: PendingSend) => {
    if (sendBusy) return;
    setSendClicks((count) => count + 1);
    setPendingSend(next);
    if (!sendGate.enabled) {
      setModal(WORKSPACE_MODAL.feedback);
      return;
    }
    setSendBusy(true);
    try {
      // Already overridden on this button: send it with the reason and close fields given, without asking again.
      const override = sendGate.overrideFor(next.markdown, next.close);
      if (override) {
        const sent = await actions.send(next.markdown, next.close, next.files, override.ticketFields);
        if (sent) setPendingSend(null);
        return;
      }
      const check = await actions.checkResponse(next.markdown);
      setModal(check?.verdict === 'block' ? WORKSPACE_MODAL.sendGate : WORKSPACE_MODAL.feedback);
    } finally {
      setSendBusy(false);
    }
  };

  /** Edit Response: back to the composer with the draft as it is; the next Send checks again. */
  const handleGateEdit = () => {
    setModal(null);
    setPendingSend(null);
  };

  const handleOverrideSubmit = async (reason: string, ticketFields: TicketFieldValue[]) => {
    if (!pendingSend || sendBusy) return;
    setSendBusy(true);
    try {
      const sent = await actions.send(pendingSend.markdown, pendingSend.close, pendingSend.files, ticketFields, reason);
      if (!sent) return;
      setModal(null);
      setPendingSend(null);
    } finally {
      setSendBusy(false);
    }
  };

  const handleFeedbackSubmit = async (
    verdict: FeedbackVerdict,
    reasons: string[],
    otherText: string | null,
    rememberScope: string | null,
    ticketFields: TicketFieldValue[],
  ) => {
    if (!pendingSend || sendBusy) return;

    setSendBusy(true);
    try {
      const feedbackRecorded = await actions.recordFeedback(
        verdict,
        reasons,
        otherText,
        rememberScope,
        pendingSend.responseId ?? undefined,
      );
      if (!feedbackRecorded) return;
      const sent = await actions.send(pendingSend.markdown, pendingSend.close, pendingSend.files, ticketFields);
      if (!sent) return;
      setModal(null);
      setPendingSend(null);
    } finally {
      setSendBusy(false);
    }
  };

  const detailsPanel = conversation ? (
    <DetailsPanel
      key={conversation.id}
      customer={conversation.customer}
      storefront={detailsStorefront!}
      subscriptions={detailsSubscriptions}
      trace={detailsTrace}
      similarTickets={{
        matches: similarTickets,
        activeConversationId: openSimilarTicket?.conversationId ?? null,
        // On a phone the details are a sheet over the conversation, where the match opens: close it first.
        onOpenTicket: (match) => {
          setMobileDetailsOpen(false);
          setOpenSimilarTicket(match);
        },
      }}
      customerTicketsFor={supportsCustomerTickets(detail?.ticket.source_provider) ? conversation.id : undefined}
      // An automation ticket: Kim sent a reply on its own in this thread.
      automated={conversation.messages.some((message) => message.isAutomated)}
      pendingActions={
        pendingActionsTurn?.pending_actions
          ? {
              actions: pendingActionsTurn.pending_actions,
              ...(canEdit && ['pending', 'partial', 'failed'].includes(pendingActionsTurn.pending_actions.status)
                ? {
                    onRun: async () => {
                      await sentinelWorkspaceService.runPostProcessing(conversation.id, pendingActionsTurn.id);
                      await refreshConversation();
                    },
                  }
                : {}),
            }
          : undefined
      }
      onCollapse={() => (isMobile ? setMobileDetailsOpen(false) : setDetailsCollapsed(true))}
      width={detailsWidth}
      onWidthChange={isMobile ? undefined : setDetailsWidth}
    />
  ) : null;

  if (view === 'automation') {
    return (
      <AutomationQueue title={viewSwitch} canEdit={canEdit} />
    );
  }

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {(isMobile ? tickets.selectedId === null : !listCollapsed) && (
        <TicketList
          className={isMobile ? 'w-full' : undefined}
          items={listItems}
          title={viewSwitch}
          isLoading={tickets.isLoading}
          search={{ value: tickets.search, onChange: tickets.setSearch, placeholder: 'Search by ticket ID' }}
          searchAction={ticketSection && <TicketStatusFilter counts={viewCounts?.[ticketSection] ?? undefined} />}
          emptyMessage={tickets.search.trim() ? 'No ticket with that number in this view.' : undefined}
          headerActions={
            <ListIconButton label="Refresh tickets" onClick={() => void tickets.refresh()} disabled={tickets.isRefreshing}>
              <RefreshCw className={tickets.isRefreshing ? 'size-[14px] animate-spin' : 'size-[14px]'} />
            </ListIconButton>
          }
          selectedId={tickets.selectedId}
          onSelect={tickets.selectTicket}
          viewersById={presence.viewersByTicket}
          paging={{ hasMore: tickets.hasMore, isFetchingNextPage: tickets.isFetchingNextPage, onLoadMore: tickets.loadMore }}
          onCollapse={() => setListCollapsed(true)}
        />
      )}

      {/* A floor, not min-w-0: it is what makes the details panel shrink first. Phones have no floor. */}
      <div
        className={cn(
          'flex flex-1 flex-col',
          isMobile ? (tickets.selectedId === null ? 'hidden' : 'min-w-0') : 'min-w-[420px]',
        )}
      >
        {conversation || conversationLoading ? (
          <>
            <ConversationHeader
              displayId={conversation?.displayId ?? null}
              helpdeskLink={conversation && <OpenInHelpdeskButton ticketId={conversation.id} />}
              listCollapsed={isMobile || listCollapsed}
              onExpandList={() => (isMobile ? tickets.selectTicket(null) : setListCollapsed(false))}
              detailsCollapsed={isMobile || detailsCollapsed}
              onExpandDetails={() => (isMobile ? setMobileDetailsOpen(true) : setDetailsCollapsed(false))}
              viewers={presence.viewers}
              actions={
                conversation ? (
                  <>
                    {assignHeaderButton(conversation.id)}
                    {unassignHeaderButton(conversation.id)}
                    {canEdit && (
                      <TicketFollowupControl
                        key={conversation.id}
                        ticketId={conversation.id}
                        customerName={conversation.customer.name}
                      />
                    )}
                  </>
                ) : null
              }
            />
            <div className="relative flex min-h-0 flex-1 flex-col bg-gray-100">
              <div ref={threadScrollRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                {conversationLoading || !conversation ? (
                  <ThreadSkeleton />
                ) : (
                  <ChatThread
                    messages={threadMessages}
                    timeZone={timeZone}
                    activityRuns={activityRunByReply}
                    selectedRunId={selectedRunId}
                    onOpenActivity={openActivity}
                  />
                )}
              </div>

              {canEdit && !conversation && conversationLoading && <ComposerSkeleton />}
              {canEdit && conversation && (
                <>
                  {/* onInput bubbles up from whichever field the composer is showing. */}
                  <div
                    ref={composerRef}
                    onInput={presence.markTyping}
                    className="absolute inset-x-0 bottom-0 flex max-h-full flex-col p-4"
                  >
                    {/* Masked so the frosting only exists at the bottom and fades out going up. */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-100 to-transparent backdrop-blur-[3px] [mask-image:linear-gradient(to_top,black_45%,transparent)]" />
                    <div className="relative flex min-h-0 flex-col">
                      <ComposerPanel
                        composer={composer}
                        actions={actions}
                        aiChat={aiChat}
                        refineLimitHit={refineLimitHit}
                        regenerateReady={regenerateReady}
                        blueprintLimitHit={blueprintLimitHit}
                        blueprintsOff={blueprintsOff}
                        sendBusy={sendBusy}
                        setSendBusy={setSendBusy}
                        onSetModal={setModal}
                        attachmentsEnabled={
                          detail?.ticket.mode === 'live' && supportsProviderAttachments(detail.ticket.source_provider)
                        }
                        onSend={(markdown, close, responseId, files) =>
                          void handleSend({ markdown, close, responseId, files })
                        }
                      />
                    </div>
                  </div>
                  {/* Outside the floating composer: an overlay nested there would be clipped to it. */}
                  <AiChatOverlayHost
                    actions={actions}
                    aiChat={aiChat}
                    versions={versions}
                    refineLimitHit={refineLimitHit}
                    refineReady={refineReady}
                  />
                </>
              )}
              {/* Not behind canEdit: reading a past ticket needs no reply rights. */}
              {openSimilarTicket && conversation && (
                <SimilarTicketPanel
                  key={openSimilarTicket.conversationId}
                  ticketId={conversation.id}
                  match={openSimilarTicket}
                  onClose={() => setOpenSimilarTicket(null)}
                />
              )}
            </div>
          </>
        ) : conversationError ? (
          <ConversationError onBack={() => tickets.selectTicket(null)} onExpandList={expandList} />
        ) : (
          <EmptyState onExpandList={expandList} />
        )}
      </div>

      {!conversation && conversationLoading && !detailsCollapsed && !isMobile && <DetailsPanelSkeleton />}
      {conversation && !detailsCollapsed && !isMobile && detailsPanel}
      {isMobile && conversation && (
        <Sheet open={mobileDetailsOpen} onOpenChange={setMobileDetailsOpen}>
          {/* The panel has its own close control at the same corner; the sheet's own would sit on top of it. */}
          <SheetContent side="right" showClose={false} className="w-[360px] max-w-full gap-0 overflow-y-auto p-0">
            <SheetTitle className="sr-only">Ticket details</SheetTitle>
            {/* The sheet sets the width: the desktop panel's own width and floor would overflow it. */}
            <div className="flex min-h-0 flex-1 [&>*]:w-full! [&>*]:min-w-0! [&>*]:border-l-0">{detailsPanel}</div>
          </SheetContent>
        </Sheet>
      )}

      {rejectVariant && conversation && (
        <RejectModal
          open
          variant={rejectVariant}
          ticketId={conversation.id}
          offersProviderAgent={detail?.ticket.mode === 'live' && supportsProviderHandoff(detail.ticket.source_provider)}
          afterError={composer.status === COMPOSER_STATUS.generationError}
          onClose={() => setModal(null)}
          onConfirm={handleRejectConfirm}
        />
      )}
      {/* The header's "View details" opens a follow-up here, not only in the Follow-ups section. */}
      <FollowupDrawer onChanged={() => void queryClient.invalidateQueries({ queryKey: ['sentinel', 'followups'] })} />
      <FeedbackForAiModal
        open={modal === WORKSPACE_MODAL.feedback}
        closeTicketFields={pendingSend?.close === true && liveGorgias}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />
      <SendGateModal
        open={modal === WORKSPACE_MODAL.sendGate}
        check={sendGate.check}
        onEdit={handleGateEdit}
        onOverride={() => setModal(WORKSPACE_MODAL.overrideReason)}
      />
      <OverrideReasonModal
        key={sendClicks}
        open={modal === WORKSPACE_MODAL.overrideReason}
        closeTicketFields={pendingSend?.close === true && liveGorgias}
        onBack={() => setModal(WORKSPACE_MODAL.sendGate)}
        onClose={handleGateEdit}
        onSubmit={handleOverrideSubmit}
      />
    </div>
  );
}

export function SentinelWorkspace() {
  const user = useAtomValue(currentUserAtom);
  const { can } = usePermissions();
  const canEdit = can('edit', 'sentinel-workspace');
  const [brand, setBrand] = useAtom(selectedBrandAtom);
  const [selectedTicketId, setSelectedTicketId] = useAtom(selectedTicketIdAtom);
  const settings = useSentinelSettingsGate();
  // The URL names the ticket before the list hook stores it.
  const { pathname, search } = useLocation();
  const hasTicket = selectedTicketId !== null || sentinelTicketIdFromUrl(pathname, new URLSearchParams(search)) !== null;

  const parsedAccountId = user?.accountId == null ? null : Number(user.accountId);
  const activeAccountId = parsedAccountId !== null && Number.isFinite(parsedAccountId) ? parsedAccountId : null;
  const activeBrand = brand?.id === activeAccountId ? brand : null;

  useEffect(() => {
    if (activeAccountId === null) {
      setSelectedTicketId(null);
      return;
    }
    // Nothing saved (first visit after login) means the account the user is logged into.
    if (!brand || brand.id !== activeAccountId) {
      setBrand({ id: activeAccountId, name: user?.accountName ?? brand?.name ?? '' });
      // Only a real account switch leaves a ticket from another account open.
      if (brand) setSelectedTicketId(null);
    }
  }, [activeAccountId, brand, setBrand, setSelectedTicketId, user?.accountName]);

  if (!activeBrand) return <WorkspaceSkeleton canEdit={canEdit} hasTicket={hasTicket} />;

  if (settings.state === 'error') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <p className="text-[15px] font-medium text-text-body">Unable to load Sentinel settings</p>
        <p className="text-[13px] leading-[18px] text-gray-800">No tickets were loaded. Try again when the connection is restored.</p>
        <Button variant="outline" onClick={settings.retry}>
          Retry
        </Button>
      </div>
    );
  }

  if (settings.state === 'loading') return <WorkspaceSkeleton canEdit={canEdit} hasTicket={hasTicket} />;

  if (!settings.enabled) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <p className="text-[15px] font-medium text-text-body">Sentinel is turned off for this brand</p>
        <p className="text-[13px] leading-[18px] text-gray-800">
          An administrator can turn it back on in Settings &rarr; Sentinel Workspace. Existing tickets are kept.
        </p>
      </div>
    );
  }

  return <EnabledSentinelWorkspace canEdit={canEdit} />;
}
